import { c as _c } from "react/compiler-runtime";
/**
 * Shared View Filter Store
 *
 * Zustand store with hybrid persistence for managing filters in shared views.
 * - Global filters (dateRange, staffGroup, properties) → URL (short, shareable)
 * - Per-visualization filters → sessionStorage (not in URL to keep URLs short)
 *
 * Supports both Simple View (global filters) and Detailed View (per-visualization filters).
 */

;
!function () {
  try {
    var e = "undefined" != typeof window ? window : "undefined" != typeof global ? global : "undefined" != typeof globalThis ? globalThis : "undefined" != typeof self ? self : {};
    e._sentryModuleMetadata = e._sentryModuleMetadata || {}, e._sentryModuleMetadata[new e.Error().stack] = Object.assign({}, e._sentryModuleMetadata[new e.Error().stack], {
      "_sentryBundlerPluginAppKey:propstrata-app": true
    });
  } catch (e) {}
}();
import LZString from "lz-string";
import qs from "qs";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import { logger } from "@/lib/services/logger";
import type { ProviderType } from "@/types/dataProvider";

// ============================================================================
// Types
// ============================================================================

export interface GlobalFilters {
  dateRange: string;
  dataProvider?: ProviderType.APP_FOOLIO | ProviderType.RENTVINE | ProviderType.QUICKBOOKS;
  /** Leaderboard staff group filter */
  staffGroup: "property-manager" | "leasing-agent" | "property-group" | "service-tech" | "all";
  selectedProperties: number[];
  selectedPropertyGroups: number[];
}
export interface VisualizationFilters {
  // Common filters
  mode?: "legacy" | "realmx";
  compareBy?: string;
  dateRange?: string;
  // Financial visualization filters (Income Statement, Ledger/Budget Performance)
  accountingBasis?: "accrual" | "cash";
  // Selection filters
  selectedProperties?: number[];
  selectedPropertyGroups?: number[];
  selectedPropertyManagers?: string[];
  // Additional chart-specific filters
  [key: string]: unknown;
}
interface SharedViewFilterState {
  /** Global filters that apply to all visualizations (Simple View) */
  global: GlobalFilters;
  /** Per-visualization filters keyed by visualization ID (Detailed View) */
  viz: Record<string, VisualizationFilters>;
}
interface SharedViewFilterActions {
  /** Set global date range filter */
  setGlobalDateRange: (dateRange: string) => void;
  /** Set global financial statements data provider filter */
  setGlobalDataProvider: (dataProvider: ProviderType.APP_FOOLIO | ProviderType.RENTVINE | ProviderType.QUICKBOOKS | undefined) => void;
  /** Set global staff group filter (Leaderboard) */
  setGlobalStaffGroup: (staffGroup: GlobalFilters["staffGroup"]) => void;
  /** Set global selected properties filter */
  setGlobalSelectedProperties: (properties: number[]) => void;
  /** Set global selected property groups filter */
  setGlobalSelectedPropertyGroups: (propertyGroups: number[]) => void;
  /** Set a specific visualization's filters */
  setVisualizationFilter: (vizId: string, filters: Partial<VisualizationFilters>) => void;
  /** Get filters for a specific visualization */
  getVisualizationFilter: (vizId: string) => VisualizationFilters | undefined;
  /** Clear all filters */
  clearFilters: () => void;
  /** Clear filters for a specific visualization */
  clearVisualizationFilter: (vizId: string) => void;
}
type SharedViewFilterStore = SharedViewFilterState & SharedViewFilterActions;

// ============================================================================
// Constants
// ============================================================================

// URL param names for shared view filters.
// NOTE: `svf` and `svfv` are legacy params that may exist in old URLs.
// We only write to `svfg` (global filters) now - viz filters go to sessionStorage.
const URL_PARAM_NAME = "svf"; // legacy: shared view filters (global + viz)
const URL_PARAM_GLOBAL = "svfg"; // shared view filters - global only
const URL_PARAM_VIZ_LEGACY = "svfv"; // legacy: viz filters (no longer written, read for migration)
const SESSION_STORAGE_KEY = "shared-view-viz-filters"; // sessionStorage key for viz filters
const DEBOUNCE_MS = 300;
const DEFAULT_DATE_RANGE = "current-month";
const DEFAULT_STAFF_GROUP: GlobalFilters["staffGroup"] = "property-manager";
const normalizeStaffGroup = (staffGroup: unknown): GlobalFilters["staffGroup"] => {
  if (typeof staffGroup !== "string") {
    return DEFAULT_STAFF_GROUP;
  }
  if (staffGroup === "property-manager" || staffGroup === "leasing-agent" || staffGroup === "property-group" || staffGroup === "service-tech" || staffGroup === "all") {
    return staffGroup;
  }
  return DEFAULT_STAFF_GROUP;
};

// Initial state
const INITIAL_STATE: SharedViewFilterState = {
  global: {
    dateRange: DEFAULT_DATE_RANGE,
    selectedProperties: [],
    selectedPropertyGroups: [],
    staffGroup: DEFAULT_STAFF_GROUP
  },
  viz: {}
};

// ============================================================================
// Hybrid Storage (URL for global, sessionStorage for viz)
// ============================================================================

const isServer = typeof window === "undefined";

// QS serialization options
const QS_STRINGIFY_OPTIONS: qs.IStringifyOptions = {
  allowDots: true,
  delimiter: ":",
  encodeValuesOnly: true,
  indices: true,
  skipNulls: true
};

// QS parse options
const QS_PARSE_OPTIONS: qs.IParseOptions = {
  allowDots: true,
  arrayLimit: 200,
  delimiter: ":",
  duplicates: "combine",
  parseArrays: true,
  plainObjects: true
};

/**
 * Coerce parsed string values to their proper types (numbers, arrays).
 */
const coerceTypes = (parsed: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (Array.isArray(value)) {
      result[key] = value.map(item => {
        if (typeof item === "string") {
          const numberValue = Number(item);
          return Number.isNaN(numberValue) ? item : numberValue;
        }
        return item;
      });
    } else if (typeof value === "object" && value !== null) {
      // Recursively coerce nested objects
      result[key] = coerceTypes(value as Record<string, unknown>);
    } else if (typeof value === "string") {
      const numberValue = Number(value);
      result[key] = Number.isNaN(numberValue) ? value : numberValue;
    } else {
      result[key] = value;
    }
  }
  return result;
};

/**
 * Creates hybrid storage for the shared view filter store.
 * - Global filters are stored in URL (svfg param) for shareability
 * - Visualization filters are stored in sessionStorage to keep URLs short
 */
const createHybridStorage = () => {
  if (isServer) {
    return {
      getItem: () => null,
      removeItem: () => {},
      setItem: () => {}
    };
  }
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let lastGlobalRef: unknown = null;
  let lastGlobalSerialized: string | null = null;
  const getSearchParams = () => new URLSearchParams(window.location.search);
  const updateUrl = (searchParams: URLSearchParams) => {
    const newUrl = `${window.location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", newUrl);
  };
  const tryDecompress = (compressedValue: string) => {
    let filterString = compressedValue;
    try {
      const decompressed = LZString.decompressFromEncodedURIComponent(compressedValue);
      if (decompressed) {
        filterString = decompressed;
      }
    } catch {
      // If decompression fails, assume uncompressed
    }
    return filterString;
  };
  const parseUrlParam = (searchParams: URLSearchParams, paramName: string) => {
    const rawValue = searchParams.get(paramName);
    if (!rawValue) return null;
    const filterString = tryDecompress(rawValue);
    const parsed = qs.parse(filterString, QS_PARSE_OPTIONS);
    const typed = coerceTypes(parsed as Record<string, unknown>);
    return typed;
  };
  const serializeParam = (value: unknown) => {
    const serialized = qs.stringify(value, QS_STRINGIFY_OPTIONS);
    const compressed = LZString.compressToEncodedURIComponent(serialized);
    return compressed.length < serialized.length ? compressed : serialized;
  };

  // Read viz filters from sessionStorage
  const getVizFromSession = (): Record<string, VisualizationFilters> => {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!stored) return {};
      return JSON.parse(stored);
    } catch {
      return {};
    }
  };

  // Write viz filters to sessionStorage
  const setVizToSession = (viz: Record<string, VisualizationFilters>) => {
    try {
      if (Object.keys(viz).length === 0) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      } else {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(viz));
      }
    } catch {
      // sessionStorage may be unavailable in some contexts
    }
  };
  return {
    getItem: (_key: string): string | null => {
      try {
        const searchParams = getSearchParams();

        // Read global filters from URL
        let globalState = INITIAL_STATE.global;
        const globalParsed = parseUrlParam(searchParams, URL_PARAM_GLOBAL);
        if (globalParsed) {
          const parsedGlobal = globalParsed as unknown as GlobalFilters;
          globalState = {
            ...parsedGlobal,
            staffGroup: normalizeStaffGroup(parsedGlobal.staffGroup)
          };
        }

        // Read viz filters from sessionStorage
        let vizState = getVizFromSession();

        // Migration: Check for legacy URL params and migrate to new storage
        // If svfv (legacy viz param) exists in URL, read it and move to sessionStorage
        const legacyVizParsed = parseUrlParam(searchParams, URL_PARAM_VIZ_LEGACY);
        if (legacyVizParsed && Object.keys(legacyVizParsed).length > 0) {
          // Merge legacy viz filters into sessionStorage
          vizState = {
            ...vizState,
            ...(legacyVizParsed as Record<string, VisualizationFilters>)
          };
          setVizToSession(vizState);

          // Clean up legacy URL param (will happen on next setItem)
        }

        // Also check for legacy combined param (svf)
        const legacyCombinedValue = searchParams.get(URL_PARAM_NAME);
        if (legacyCombinedValue && !globalParsed) {
          const filterString = tryDecompress(legacyCombinedValue);
          const parsed = qs.parse(filterString, QS_PARSE_OPTIONS);
          const typed = coerceTypes(parsed as Record<string, unknown>);
          if (typed.global) {
            const parsedGlobal = typed.global as GlobalFilters;
            globalState = {
              ...parsedGlobal,
              staffGroup: normalizeStaffGroup(parsedGlobal.staffGroup)
            };
          }
          if (typed.viz) {
            vizState = {
              ...vizState,
              ...(typed.viz as Record<string, VisualizationFilters>)
            };
            setVizToSession(vizState);
          }
        }
        const state = {
          global: globalState,
          viz: vizState
        };

        // Return in Zustand persist format
        return JSON.stringify({
          state,
          version: 0
        });
      } catch (error) {
        logger.error("[sharedViewFilterStore] Error reading state", error instanceof Error ? error : new Error(String(error)), {
          feature: "shared-view-filter-store"
        });
        return null;
      }
    },
    removeItem: (_key: string): void => {
      try {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
        const searchParams = getSearchParams();
        // Clean up all URL params (current and legacy)
        searchParams.delete(URL_PARAM_NAME);
        searchParams.delete(URL_PARAM_GLOBAL);
        searchParams.delete(URL_PARAM_VIZ_LEGACY);
        updateUrl(searchParams);

        // Clear sessionStorage
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      } catch (error) {
        logger.error("[sharedViewFilterStore] Error removing state", error instanceof Error ? error : new Error(String(error)), {
          feature: "shared-view-filter-store"
        });
      }
    },
    setItem: (_key: string, value: string): void => {
      try {
        const parsed = JSON.parse(value);
        const state = parsed.state || {};

        // Write viz filters immediately so refresh doesn't lose changes.
        // URL updates can stay debounced, but sessionStorage should be instant.
        const vizState = state.viz ?? {};
        setVizToSession(vizState);
        const doUpdateUrl = () => {
          const searchParams = getSearchParams();

          // Check if global state is default
          const isDefaultGlobal = !state.global || state.global.dateRange === DEFAULT_DATE_RANGE && normalizeStaffGroup(state.global.staffGroup) === DEFAULT_STAFF_GROUP && (!state.global.selectedProperties || state.global.selectedProperties.length === 0) && (!state.global.selectedPropertyGroups || state.global.selectedPropertyGroups.length === 0);

          // Always clean up legacy URL params
          searchParams.delete(URL_PARAM_NAME);
          searchParams.delete(URL_PARAM_VIZ_LEGACY);

          // Handle global filters → URL
          if (isDefaultGlobal) {
            searchParams.delete(URL_PARAM_GLOBAL);
            lastGlobalSerialized = null;
            lastGlobalRef = null;
          } else {
            const globalState = state.global ?? INITIAL_STATE.global;
            if (lastGlobalRef !== globalState || !lastGlobalSerialized) {
              lastGlobalSerialized = serializeParam(globalState);
              lastGlobalRef = globalState;
            }
            searchParams.set(URL_PARAM_GLOBAL, lastGlobalSerialized);
          }
          updateUrl(searchParams);
        };
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
        debounceTimer = setTimeout(doUpdateUrl, DEBOUNCE_MS);
      } catch (error) {
        logger.error("[sharedViewFilterStore] Error writing state", error instanceof Error ? error : new Error(String(error)), {
          feature: "shared-view-filter-store"
        });
      }
    }
  };
};

// ============================================================================
// Store
// ============================================================================

export const useSharedViewFilterStore = create<SharedViewFilterStore>()(devtools(persist((set, get) => ({
  clearFilters: () => {
    set(INITIAL_STATE, false, "clearFilters");
  },
  clearVisualizationFilter: vizId => {
    set(state => {
      const {
        [vizId]: _,
        ...rest
      } = state.viz;
      return {
        viz: rest
      };
    }, false, "clearVisualizationFilter");
  },
  getVisualizationFilter: vizId => {
    return get().viz[vizId];
  },
  global: INITIAL_STATE.global,
  setGlobalDataProvider: dataProvider => {
    set(state => {
      if (dataProvider === undefined) {
        if (state.global.dataProvider === undefined) {
          return state;
        }
        const {
          dataProvider: _dataProvider,
          ...global
        } = state.global;
        return {
          global
        };
      }
      if (state.global.dataProvider === dataProvider) {
        return state;
      }
      return {
        global: {
          ...state.global,
          dataProvider
        }
      };
    }, false, "setGlobalDataProvider");
  },
  setGlobalDateRange: dateRange => {
    set(state => ({
      global: {
        ...state.global,
        dateRange
      }
    }), false, "setGlobalDateRange");
  },
  setGlobalSelectedProperties: selectedProperties => {
    set(state => ({
      global: {
        ...state.global,
        selectedProperties
      }
    }), false, "setGlobalSelectedProperties");
  },
  setGlobalSelectedPropertyGroups: selectedPropertyGroups => {
    set(state => ({
      global: {
        ...state.global,
        selectedPropertyGroups
      }
    }), false, "setGlobalSelectedPropertyGroups");
  },
  setGlobalStaffGroup: staffGroup => {
    set(state => ({
      global: {
        ...state.global,
        staffGroup: normalizeStaffGroup(staffGroup)
      }
    }), false, "setGlobalStaffGroup");
  },
  setVisualizationFilter: (vizId, filters) => {
    set(state => ({
      viz: {
        ...state.viz,
        [vizId]: {
          ...state.viz[vizId],
          ...filters
        }
      }
    }), false, "setVisualizationFilter");
  },
  viz: INITIAL_STATE.viz
}), {
  name: "shared-view-filters",
  partialize: state => ({
    global: state.global,
    viz: state.viz
  }),
  storage: createJSONStorage(() => createHybridStorage())
}), {
  name: "shared-view-filter-store"
}));

// ============================================================================
// Selector Hooks (for optimized re-renders)
// ============================================================================

/**
 * Get global filters
 */
export const useGlobalFilters = () => {
  return useSharedViewFilterStore(_temp);
};

/**
 * Get global date range
 */
export const useGlobalDateRange = () => {
  return useSharedViewFilterStore(_temp2);
};

/**
 * Get global financial statements data provider
 */
export const useGlobalDataProvider = () => {
  return useSharedViewFilterStore(_temp3);
};

/**
 * Get global staff group (Leaderboard)
 */
export const useGlobalStaffGroup = () => {
  return useSharedViewFilterStore(_temp4);
};

/**
 * Get global selected properties
 */
export const useGlobalSelectedProperties = () => {
  return useSharedViewFilterStore(_temp5);
};

/**
 * Get global selected property groups
 */
export const useGlobalSelectedPropertyGroups = () => {
  return useSharedViewFilterStore(_temp6);
};

/**
 * Get filters for a specific visualization
 */
export const useVisualizationFilters = vizId => {
  const $ = _c(2);
  let t0;
  if ($[0] !== vizId) {
    t0 = state => state.viz[vizId];
    $[0] = vizId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useSharedViewFilterStore(t0);
};

/**
 * Get filter actions
 */
export const useSharedViewFilterActions = () => {
  return useSharedViewFilterStore(useShallow(_temp7));
};
function _temp(state) {
  return state.global;
}
function _temp2(state) {
  return state.global.dateRange;
}
function _temp3(state) {
  return state.global.dataProvider;
}
function _temp4(state) {
  return state.global.staffGroup;
}
function _temp5(state) {
  return state.global.selectedProperties;
}
function _temp6(state) {
  return state.global.selectedPropertyGroups;
}
function _temp7(state) {
  return {
    clearFilters: state.clearFilters,
    clearVisualizationFilter: state.clearVisualizationFilter,
    getVisualizationFilter: state.getVisualizationFilter,
    setGlobalDataProvider: state.setGlobalDataProvider,
    setGlobalDateRange: state.setGlobalDateRange,
    setGlobalSelectedProperties: state.setGlobalSelectedProperties,
    setGlobalSelectedPropertyGroups: state.setGlobalSelectedPropertyGroups,
    setGlobalStaffGroup: state.setGlobalStaffGroup,
    setVisualizationFilter: state.setVisualizationFilter
  };
}